// MARCA | 07/05/2026 | New file – Style With section custom element (card selection, variant picking, add to cart)
import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Custom element powering the "Style With" PDP section.
 * Handles card selection, variant/size picking, and add-to-cart functionality.
 *
 * @extends {Component}
 */
class StyleWithSection extends Component {
  /** @type {Map<string, {id: number, title: string, available: boolean, variants: Array<{id: number, title: string, available: boolean, price: number, compare_at_price: number|null, options: string[]}>}>} */
  #productData = new Map();

  /** @type {string|null} Currently selected product ID */
  #activeProductId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#parseProductData();
    this.#bindCardSelection();
    this.#bindSizeButtons();
    this.#bindAddToCartButtons();
  }

  /**
   * Parse embedded JSON scripts to build a product data map for variant resolution.
   */
  #parseProductData() {
    const scripts = this.querySelectorAll('script[data-style-with-product-json]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);
        this.#productData.set(String(data.id), data);
        if (!this.#activeProductId) {
          this.#activeProductId = String(data.id);
        }
      } catch (e) {
        console.error('[StyleWith] Failed to parse product JSON:', e);
      }
    }
  }

  /**
   * Bind click handlers on product cards for selection toggling.
   */
  #bindCardSelection() {
    const cards = this.querySelectorAll('[data-style-with-card]');
    cards.forEach((el) => {
      const card = /** @type {HTMLElement} */ (el);
      card.addEventListener('click', () => {
        const productId = card.dataset.styleWithCard || '';
        if (productId === this.#activeProductId) return;
        this.#selectProduct(productId);
      });
    });
  }

  /**
   * Activate a product by ID – update cards, featured image, and detail panel.
   * @param {string} productId
   */
  #selectProduct(productId) {
    this.#activeProductId = productId;

    // Update card active states
    this.querySelectorAll("[data-style-with-card]").forEach((el) => {
      const card = /** @type {HTMLElement} */ (el);
      const isActive = card.dataset.styleWithCard === productId;
      card.classList.toggle("style-with__card--active", isActive);
      card.setAttribute("aria-pressed", String(isActive));
    });

    // MARCA | 20/05/2026 | Featured image toggling removed – hero image is now static from designer metaobject

    // Update detail panels
    this.querySelectorAll("[data-style-with-detail]").forEach((el) => {
      const detail = /** @type {HTMLElement} */ (el);
      detail.classList.toggle(
        "style-with__detail--active",
        detail.dataset.styleWithDetail === productId,
      );
    });
  }

  /**
   * Bind size/variant option buttons within each detail panel.
   */
  #bindSizeButtons() {
    const sizeButtons = this.querySelectorAll('.style-with__size-btn');
    sizeButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => this.#handleSizeSelect(e));
    });
  }

  /**
   * Handle size button selection – update selected state and resolve the matching variant.
   * @param {Event} event
   */
  #handleSizeSelect(event) {
    const btn = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const optionContainer = btn.closest('.style-with__option');
    if (!optionContainer) return;

    // Deselect siblings, select this one
    optionContainer.querySelectorAll('.style-with__size-btn').forEach((sibling) => {
      sibling.classList.remove('style-with__size-btn--selected');
    });
    btn.classList.add('style-with__size-btn--selected');

    // Resolve the new variant
    const detailPanel = btn.closest('[data-style-with-detail]');
    if (!detailPanel) return;

    const productId = /** @type {HTMLElement} */ (detailPanel).dataset.styleWithDetail || '';
    this.#resolveVariant(productId, detailPanel);
  }

  /**
   * Given the current option selections in a detail panel, find the matching variant
   * and update the add-to-cart button accordingly.
   * @param {string} productId
   * @param {Element} detailPanel
   */
  #resolveVariant(productId, detailPanel) {
    const productData = this.#productData.get(productId);
    if (!productData) return;

    // Gather selected options in order
    /** @type {string[]} */
    const selectedOptions = [];
    const optionGroups = detailPanel.querySelectorAll('.style-with__option');
    optionGroups.forEach((group) => {
      const selectedBtn = /** @type {HTMLElement|null} */ (group.querySelector('.style-with__size-btn--selected'));
      if (selectedBtn && selectedBtn.dataset.optionValue) {
        selectedOptions.push(selectedBtn.dataset.optionValue);
      }
    });

    // Find matching variant
    const matchedVariant = productData.variants.find((variant) =>
      variant.options.every((opt, idx) => opt === selectedOptions[idx]),
    );

    // Update add-to-cart button
    const addBtn = /** @type {HTMLButtonElement|null} */ (detailPanel.querySelector('.style-with__add-to-cart'));
    if (addBtn && matchedVariant) {
      addBtn.dataset.variantId = String(matchedVariant.id);
      addBtn.disabled = !matchedVariant.available;
      const textEl = addBtn.querySelector('.style-with__add-to-cart-text');
      if (textEl) {
        textEl.textContent = matchedVariant.available ? 'ADD TO CART' : 'SOLD OUT';
      }
    } else if (addBtn) {
      addBtn.disabled = true;
      const textEl = addBtn.querySelector('.style-with__add-to-cart-text');
      if (textEl) textEl.textContent = 'UNAVAILABLE';
    }
  }

  /**
   * Bind add-to-cart buttons across all detail panels.
   */
  #bindAddToCartButtons() {
    const buttons = this.querySelectorAll('.style-with__add-to-cart');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => this.#handleAddToCart(e));
    });
  }

  /**
   * Handle add-to-cart via the Shopify Cart API.
   * @param {Event} event
   */
  async #handleAddToCart(event) {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    const textEl = button.querySelector('.style-with__add-to-cart-text');
    const originalText = textEl?.textContent || '';
    if (textEl) textEl.textContent = 'ADDING...';

    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const routes = shopify?.routes?.root || '/';

      const response = await fetch(routes + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(variantId, 10),
              quantity: 1,
              // MARCA | 09/10/2026 | Hidden line property so Style With sales can be found on orders
              properties: { _upsell_source: 'style_with' },
            },
          ],
        }),
      });

      if (!response.ok) throw new Error('Failed to add product to cart');

      await response.json();

      const cartResponse = await fetch(routes + 'cart.js');
      const cartData = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cartData, 'style-with', {
          itemCount: cartData.item_count,
          source: 'style-with',
        }),
      );

      if (textEl) textEl.textContent = 'ADDED!';
      setTimeout(() => {
        button.disabled = false;
        if (textEl) textEl.textContent = originalText;
      }, 1500);
    } catch (error) {
      console.error('[StyleWith] Failed to add to cart:', error);
      button.disabled = false;
      if (textEl) textEl.textContent = originalText;
    }
  }
}

if (!customElements.get('style-with-section')) {
  customElements.define('style-with-section', StyleWithSection);
}
